import { ref, computed } from 'vue'

const blueChart = Object.freeze({
  blueChart1000: '#142459',
  blueChart1010: '#174060',
  blueChart1020: '#264FA3',
  blueChart1030: '#176BA0',
  blueChart1040: '#377CB7',
  blueChart1050: '#3474EB',
  blueChart1060: '#19AADE',
  blueChart1070: '#44A5C2',
  blueChart1080: '#1AC9E6',
  blueChart1090: '#72C8F2',
  blueChart2000: '#CFEBF9'
})

const tealChart = Object.freeze({
  tealChart1000: '#1999C3',
  tealChart1010: '#19B5C3',
  tealChart1020: '#1BD4D4',
  tealChart1030: '#1DE4BD',
  tealChart1040: '#6DF0D2',
  tealChart1050: '#C7F9EE'
})

const purpleChart = Object.freeze({
  purpleChart1010: '#29066B',
  purpleChart1020: '#570088',
  purpleChart1030: '#482B8B',
  purpleChart1040: '#7D298B',
  purpleChart1050: '#7A30AB',
  purpleChart1060: '#7D3AC1',
  purpleChart1070: '#4911F6',
  purpleChart1080: '#721AF2',
  purpleChart1090: '#801FEF',
  purpleChart2000: '#B002B3',
  purpleChart2010: '#BA55D3',
  purpleChart2020: '#D8BFD8'
})

const redChart = Object.freeze({
  redChart1000: '#820401',
  redChart1010: '#9C0501',
  redChart1020: '#C02323',
  redChart1030: '#DA313F',
  redChart1040: '#DE542C',
  redChart1050: '#C5580F',
  redChart1060: '#EC553A',
  redChart1070: '#EE6F19',
  redChart1080: '#EF7E32',
  redChart1090: '#EE9319',
  redChart2000: '#F5BE74'
})

const greenChart = Object.freeze({
  greenChart1000: '#305A4C',
  greenChart1010: '#25723B',
  greenChart1020: '#1C862D',
  greenChart1030: '#08B30F',
  greenChart1040: '#61CE0A',
  greenChart1050: '#BAEA04',
  sageChart1060: '#5FAB6D',
  sageChart1070: '#8AC777',
  sageChart1080: '#A6D090',
  sageChart1090: '#CCE9B7'
})

const brownChart = Object.freeze({
  khakiChart1000: '#5F3B21',
  khakiChart1010: '#704E33',
  khakiChart1020: '#8A6B51',
  khakiChart1030: '#A48B6D',
  mudChart1040: '#985F28',
  mudChart1050: '#CD853F',
  mudChart1060: '#EE9A3A',
  mudChart1070: '#FFAE49'
})

const yellowChart = Object.freeze({
  yellowChart1000: '#EABD3B',
  yellowChart1010: '#FFCA00',
  yellowChart1020: '#FFDC00',
  yellowChart1030: '#FFE64E',
  yellowChart1040: '#FFEF89'
})

const pinkChart = Object.freeze({
  pinkChart1000: '#B03060',
  pinkChart1010: '#EC3B71',
  pinkChart1020: '#EF5FB0',
  pinkChart1030: '#F8B8DC',
  pinkChart1040: '#DB4CB2',
  pinkChart1050: '#EB48CE',
  pinkChart1060: '#F479F4',
  pinkChart1070: '#FBCCFB'
})

const peachChart = Object.freeze({
  peachChart1000: '#EA7369',
  peachChart1010: '#F99C9C',
  peachChart1030: '#F0A58F',
  peachChart1040: '#FCEAE6'
})

const grayChart = Object.freeze({
  grayChart1000: '#797979',
  grayChart1010: '#C1C1C1',
  grayChart1020: '#E5E5E5',
  warmGrayChart1030: '#A8A5A3',
  warmGrayChart1040: '#CECCCB',
  warmGrayChart1050: '#E1E0DF',
  coolGrayChart1060: '#8F97AD',
  coolGrayChart1070: '#D3D6DF',
  coolGrayChart1080: '#EAEBF0'
})

const colors = ref({
  blueChart,
  tealChart,
  purpleChart,
  redChart,
  greenChart,
  brownChart,
  yellowChart,
  pinkChart,
  peachChart,
  grayChart
})

export function useChartColors() {
  const colorGroups = computed(() => Object.keys(colors.value))
  const colorTones = computed(() => {
    const result: any = []
    Object.values(colors.value).forEach((element) => {
      result.push(Object.values(element))
    })
    return result
  })

  const getDefinedColors = computed(() => {
    const result = []
    result.push(colors.value.purpleChart.purpleChart1010)
    result.push(colors.value.redChart.redChart1040)
    result.push(colors.value.tealChart.tealChart1030)
    result.push(colors.value.yellowChart.yellowChart1030)
    result.push(colors.value.greenChart.greenChart1010)
    result.push(colors.value.blueChart.blueChart1050)
    result.push(colors.value.grayChart.grayChart1010)
    result.push(colors.value.pinkChart.pinkChart1050)
    result.push(colors.value.brownChart.mudChart1040)
    result.push(colors.value.tealChart.tealChart1010)
    result.push(colors.value.purpleChart.purpleChart2020)
    result.push(colors.value.redChart.redChart1020)
    result.push(colors.value.grayChart.warmGrayChart1030)
    result.push(colors.value.brownChart.khakiChart1000)
    result.push(colors.value.greenChart.greenChart1040)
    result.push(colors.value.brownChart.mudChart1060)
    result.push(colors.value.grayChart.grayChart1000)
    result.push(colors.value.pinkChart.pinkChart1070)
    result.push(colors.value.purpleChart.purpleChart1080)
    result.push(colors.value.yellowChart.yellowChart1040)
    result.push(colors.value.purpleChart.purpleChart1020)
    result.push(colors.value.greenChart.sageChart1080)
    result.push(colors.value.brownChart.mudChart1070)
    result.push(colors.value.blueChart.blueChart1090)
    result.push(colors.value.peachChart.peachChart1010)
    result.push(colors.value.brownChart.khakiChart1010)
    result.push(colors.value.blueChart.blueChart1030)
    result.push(colors.value.greenChart.greenChart1050)
    result.push(colors.value.pinkChart.pinkChart1010)
    result.push(colors.value.grayChart.warmGrayChart1040)
    result.push(colors.value.blueChart.blueChart1000)
    result.push(colors.value.yellowChart.yellowChart1000)
    result.push(colors.value.tealChart.tealChart1040)
    result.push(colors.value.purpleChart.purpleChart1070)
    result.push(colors.value.redChart.redChart1070)
    result.push(colors.value.greenChart.sageChart1070)
    result.push(colors.value.purpleChart.purpleChart1060)
    result.push(colors.value.brownChart.khakiChart1030)
    result.push(colors.value.pinkChart.pinkChart1040)
    result.push(colors.value.peachChart.peachChart1040)
    result.push(colors.value.blueChart.blueChart1010)
    result.push(colors.value.redChart.redChart1050)
    result.push(colors.value.grayChart.coolGrayChart1060)
    result.push(colors.value.tealChart.tealChart1020)
    result.push(colors.value.pinkChart.pinkChart1030)
    result.push(colors.value.greenChart.sageChart1090)
    result.push(colors.value.purpleChart.purpleChart1090)
    result.push(colors.value.blueChart.blueChart2000)
    result.push(colors.value.redChart.redChart2000)
    result.push(colors.value.blueChart.blueChart1020)
    result.push(colors.value.pinkChart.pinkChart1060)
    result.push(colors.value.tealChart.tealChart1050)
    result.push(colors.value.brownChart.khakiChart1020)
    result.push(colors.value.redChart.redChart1060)
    result.push(colors.value.yellowChart.yellowChart1020)
    result.push(colors.value.purpleChart.purpleChart1040)
    result.push(colors.value.tealChart.tealChart1000)
    result.push(colors.value.redChart.redChart1030)
    result.push(colors.value.brownChart.mudChart1050)
    result.push(colors.value.greenChart.greenChart1020)
    result.push(colors.value.purpleChart.purpleChart1030)
    result.push(colors.value.blueChart.blueChart1080)
    result.push(colors.value.pinkChart.pinkChart1000)
    result.push(colors.value.grayChart.coolGrayChart1080)
    result.push(colors.value.peachChart.peachChart1000)
    result.push(colors.value.blueChart.blueChart1060)
    result.push(colors.value.greenChart.sageChart1060)
    result.push(colors.value.purpleChart.purpleChart2000)
    result.push(colors.value.grayChart.grayChart1020)
    result.push(colors.value.redChart.redChart1000)
    result.push(colors.value.greenChart.greenChart1030)
    result.push(colors.value.redChart.redChart1080)
    result.push(colors.value.grayChart.coolGrayChart1070)
    result.push(colors.value.purpleChart.purpleChart1050)
    result.push(colors.value.blueChart.blueChart1070)
    result.push(colors.value.peachChart.peachChart1030)
    result.push(colors.value.greenChart.greenChart1000)
    result.push(colors.value.yellowChart.yellowChart1010)
    result.push(colors.value.pinkChart.pinkChart1020)
    result.push(colors.value.grayChart.warmGrayChart1050)
    result.push(colors.value.redChart.redChart1010)
    result.push(colors.value.blueChart.blueChart1040)
    result.push(colors.value.purpleChart.purpleChart2010)
    result.push(colors.value.redChart.redChart1090)
    return result
  })

  function getColors(n: number) {
    const colorsLength = getDefinedColors.value.length
    const result = [] as string[]
    if (n <= colorsLength) {
      return getDefinedColors.value.slice(0, n)
    } else {
      const rounds = Math.floor(n / colorsLength)
      const remainder = n % colorsLength
      for (let i = 0; i < rounds; i++) {
        result.push(...getDefinedColors.value)
      }
      result.push(...getDefinedColors.value.slice(0, remainder))
    }
    return result
  }

  function getNamedColorByCode(code: string) {
    const color: any = Object.entries(colors.value).find((element: any) => {
      return Object.keys(element[1]).find((key) => element[1][key] === code)
    })
    if (color) {
      return Object.keys(color[1]).find((key) => color[1][key] === code)
    }
    return ''
  }

  return { getColors, getNamedColorByCode }
}

export default Object.freeze({
  blueChart,
  tealChart,
  purpleChart,
  redChart,
  greenChart,
  brownChart,
  yellowChart,
  pinkChart,
  peachChart,
  grayChart
})
